<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Header Premium -->
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-wrapper">
            <i class="fas fa-clipboard-check"></i>
          </div>
          <div class="header-text">
            <h3>🔍 Contrôle Financier</h3>
            <p>Validation et vérification de caisse</p>
          </div>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Contenu -->
      <div class="modal-body">
        <!-- Informations caisse -->
        <div class="caisse-info-section">
          <h4>📋 Informations de la Caisse</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Technicien :</span>
              <span class="value">{{ caisse?.technicien?.prenom }} {{ caisse?.technicien?.nom }}</span>
            </div>
            <div class="info-item">
              <span class="label">Date :</span>
              <span class="value">{{ formatDate(caisse?.date) }}</span>
            </div>
            <div class="info-item">
              <span class="label">Statut :</span>
              <span class="value badge" :class="getStatutClass(caisse?.statut)">{{ caisse?.statut }}</span>
            </div>
          </div>
        </div>

        <!-- Comparaison montants -->
        <div class="comparaison-section">
          <h4>💰 Comparaison des Montants</h4>
          <div class="comparaison-grid">
            <!-- Espèces -->
            <div class="comparaison-card">
              <h5>💵 Espèces</h5>
              <div class="montant-row">
                <span class="label">Attendu :</span>
                <span class="value expected">{{ formatCurrency(caisse?.montantCashAttendu) }}</span>
              </div>
              <div class="montant-row">
                <span class="label">Déclaré :</span>
                <span class="value declared">{{ formatCurrency(caisse?.montantCashDeclare) }}</span>
              </div>
              <div class="montant-row ecart" :class="getEcartClass(caisse?.ecartCash)">
                <span class="label">Écart :</span>
                <span class="value">{{ formatCurrency(Math.abs(caisse?.ecartCash || 0)) }}</span>
                <span class="type">{{ getEcartType(caisse?.ecartCash) }}</span>
              </div>
            </div>

            <!-- Chèques -->
            <div class="comparaison-card">
              <h5>📝 Chèques</h5>
              <div class="montant-row">
                <span class="label">Attendu :</span>
                <span class="value expected">{{ formatCurrency(caisse?.montantChequesAttendu) }}</span>
              </div>
              <div class="montant-row">
                <span class="label">Déclaré :</span>
                <span class="value declared">{{ formatCurrency(caisse?.montantChequesDeclare) }}</span>
              </div>
              <div class="montant-row ecart" :class="getEcartClass(caisse?.ecartCheques)">
                <span class="label">Écart :</span>
                <span class="value">{{ formatCurrency(Math.abs(caisse?.ecartCheques || 0)) }}</span>
                <span class="type">{{ getEcartType(caisse?.ecartCheques) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Validation financière -->
        <div class="validation-section">
          <h4>✅ Validation Financière</h4>
          
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-flag"></i>
              Statut de Validation *
            </label>
            <select v-model="form.statut" class="form-select" required>
              <option value="">Sélectionner un statut</option>
              <option value="Vérifiée">✅ Vérifiée - Conforme</option>
              <option value="Terminée">🔒 Terminée - Définitivement validée</option>
            </select>
          </div>

          <div v-if="hasEcarts" class="form-group">
            <label class="form-label">
              <i class="fas fa-exclamation-triangle"></i>
              Motif des Écarts
            </label>
            <textarea 
              v-model="form.motifEcart"
              class="form-textarea"
              rows="3"
              placeholder="Expliquez les raisons des écarts constatés..."
            ></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-comment"></i>
              Commentaires Financier
            </label>
            <textarea 
              v-model="form.commentairesFinancier"
              class="form-textarea"
              rows="3"
              placeholder="Ajoutez vos observations en tant que financier..."
            ></textarea>
          </div>
        </div>

        <!-- Signature financier -->
        <div class="signature-section">
          <h4>✍️ Signature Financier</h4>
          <div class="signature-container">
            <canvas 
              ref="signatureCanvas"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @touchstart="startDrawing"
              @touchmove="draw"
              @touchend="stopDrawing"
              class="signature-canvas"
              width="400"
              height="120"
            ></canvas>
            <div class="signature-controls">
              <button @click="clearSignature" class="btn-clear">
                <i class="fas fa-eraser"></i>
                Effacer
              </button>
              <div v-if="hasSignature" class="signature-status">
                <i class="fas fa-check-circle"></i>
                Signature capturée
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn-cancel">
          <i class="fas fa-times"></i>
          Annuler
        </button>
        <button 
          v-if="caisse.statut === 'Vérifiée'"
          @click="genererRecu" 
          class="btn-recu"
        >
          <i class="fas fa-file-pdf"></i>
          Voir le Reçu
        </button>
        <button 
          v-if="caisse.statut === 'Vérifiée'"
          @click="cloturerCaisse" 
          class="btn-cloturer"
        >
          <i class="fas fa-lock"></i>
          Clôturer Définitivement
        </button>
        <button 
          v-if="caisse.statut === 'Remise'"
          @click="submitValidation" 
          :disabled="!canSubmit || loading"
          class="btn-submit"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-check-circle"></i>
          {{ loading ? 'Validation...' : 'Valider la Caisse' }}
        </button>
      </div>
    </div>

    <!-- Modal Reçu -->
    <RecuCaisseModal 
      v-if="showRecuModal && recuData"
      :recu="recuData"
      @close="showRecuModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import RecuCaisseModal from './RecuCaisseModal.vue'

export default {
  name: 'ControleFinanceModal',
  components: {
    RecuCaisseModal
  },
  props: {
    caisse: Object
  },
  emits: ['close', 'validation-completed'],
  setup(props, { emit }) {
    const toast = useToast()
    const loading = ref(false)
    const signatureCanvas = ref(null)
    const isDrawing = ref(false)
    const hasSignature = ref(false)
    const showRecuModal = ref(false)
    const recuData = ref(null)

    const form = ref({
      statut: '',
      motifEcart: '',
      commentairesFinancier: ''
    })

    const hasEcarts = computed(() => {
      return (props.caisse?.ecartCash !== 0) || (props.caisse?.ecartCheques !== 0)
    })

    const canSubmit = computed(() => {
      return form.value.statut && hasSignature.value
    })

    // Gestion signature
    const setupCanvas = () => {
      const canvas = signatureCanvas.value
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.strokeStyle = '#001BB7'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
    }

    const getEventPos = (e) => {
      const canvas = signatureCanvas.value
      const rect = canvas.getBoundingClientRect()
      const clientX = e.clientX || (e.touches && e.touches[0].clientX)
      const clientY = e.clientY || (e.touches && e.touches[0].clientY)
      return {
        x: clientX - rect.left,
        y: clientY - rect.top
      }
    }

    const startDrawing = (e) => {
      e.preventDefault()
      isDrawing.value = true
      const pos = getEventPos(e)
      const ctx = signatureCanvas.value.getContext('2d')
      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
    }

    const draw = (e) => {
      if (!isDrawing.value) return
      e.preventDefault()
      const pos = getEventPos(e)
      const ctx = signatureCanvas.value.getContext('2d')
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
      hasSignature.value = true
    }

    const stopDrawing = (e) => {
      if (!isDrawing.value) return
      e.preventDefault()
      isDrawing.value = false
    }

    const clearSignature = () => {
      const canvas = signatureCanvas.value
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      hasSignature.value = false
    }

    const submitValidation = async () => {
      if (!canSubmit.value) return

      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const signatureData = signatureCanvas.value.toDataURL()

        const response = await fetch(`/api/finance/caisse/${props.caisse._id}/valider`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...form.value,
            signatureFinancier: signatureData
          })
        })

        if (response.ok) {
          const data = await response.json()
          toast.success('✅ Caisse validée avec succès')
          emit('validation-completed', data.caisse)
          closeModal()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la validation')
        }
      } catch (error) {
        console.error('Erreur validation:', error)
        toast.error('Erreur lors de la validation')
      } finally {
        loading.value = false
      }
    }

    const genererRecu = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/finance/caisse/${props.caisse._id}/recu`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          recuData.value = data.recu
          showRecuModal.value = true
        } else {
          toast.error('Erreur lors de la génération du reçu')
        }
      } catch (error) {
        console.error('Erreur reçu:', error)
        toast.error('Erreur lors de la génération du reçu')
      }
    }

    const cloturerCaisse = async () => {
      if (!confirm('Êtes-vous sûr de vouloir clôturer définitivement cette caisse ? Cette action est irréversible.')) {
        return
      }

      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/finance/caisse/${props.caisse._id}/cloturer`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          toast.success('✅ Caisse Terminée définitivement')
          emit('validation-completed', data.caisse)
          closeModal()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la clôture')
        }
      } catch (error) {
        console.error('Erreur clôture:', error)
        toast.error('Erreur lors de la clôture')
      }
    }

    const closeModal = () => {
      emit('close')
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('fr-FR')
    }

    const getStatutClass = (statut) => {
      const classes = {
        'Ouverte': 'status-open',
        'Remise': 'status-pending',
        'Vérifiée': 'status-verified',
        'Terminée': 'status-closed'
      }
      return classes[statut] || ''
    }

    const getEcartClass = (ecart) => {
      if (ecart > 0) return 'surplus'
      if (ecart < 0) return 'deficit'
      return 'equilibre'
    }

    const getEcartType = (ecart) => {
      if (ecart > 0) return 'Surplus'
      if (ecart < 0) return 'Déficit'
      return 'Équilibre'
    }

    onMounted(() => {
      setupCanvas()
    })

    return {
      form,
      loading,
      signatureCanvas,
      hasSignature,
      hasEcarts,
      canSubmit,
      startDrawing,
      draw,
      stopDrawing,
      clearSignature,
      submitValidation,
      genererRecu,
      cloturerCaisse,
      closeModal,
      showRecuModal,
      recuData,
      formatCurrency,
      formatDate,
      getStatutClass,
      getEcartClass,
      getEcartType
    }
  }
}
</script>

<style scoped>
/* Styles similaires à RemisePhysiqueModal avec adaptations pour le contrôle financier */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  z-index: 1;
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  font-size: 1.8rem;
}

.header-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 700;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.modal-body {
  padding: 2rem;
}

.caisse-info-section,
.comparaison-section,
.validation-section,
.signature-section {
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 27, 183, 0.1);
}

.caisse-info-section h4,
.comparaison-section h4,
.validation-section h4,
.signature-section h4 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1.1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-open { background: #fef3c7; color: #92400e; }
.status-pending { background: #fed7aa; color: #ea580c; }
.status-verified { background: #d1fae5; color: #065f46; }
.status-closed { background: #e0e7ff; color: #3730a3; }

.comparaison-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.comparaison-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
}

.comparaison-card h5 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-blue);
}

.montant-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding: 0.5rem 0;
}

.montant-row.ecart {
  border-top: 2px solid #e2e8f0;
  margin-top: 1rem;
  padding-top: 1rem;
  font-weight: 700;
}

.montant-row.ecart.surplus {
  color: #10b981;
  background: rgba(16, 185, 129, 0.05);
  border-radius: 8px;
  padding: 0.75rem;
}

.montant-row.ecart.deficit {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.05);
  border-radius: 8px;
  padding: 0.75rem;
}

.montant-row.ecart.equilibre {
  color: #6b7280;
}

.expected { color: #10b981; }
.declared { color: var(--primary-blue); }

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.9rem;
}

.form-select,
.form-textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.1);
}

.signature-container {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  border: 2px solid rgba(0, 27, 183, 0.1);
}

.signature-canvas {
  width: 100%;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  cursor: crosshair;
  background: #fafafa;
}

.signature-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.btn-clear {
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.signature-status {
  color: #10b981;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-footer {
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.5);
  border-top: 1px solid rgba(0, 27, 183, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-radius: 0 0 20px 20px;
}

.btn-cancel,
.btn-submit,
.btn-recu,
.btn-cloturer {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-submit {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-recu {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.btn-cloturer {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.btn-cancel:hover,
.btn-submit:hover:not(:disabled),
.btn-recu:hover,
.btn-cloturer:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ========== RESPONSIVE MOBILE ========== */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal-container {
    width: 100%;
    max-width: 100%;
    max-height: 95vh;
    border-radius: 20px 20px 0 0;
  }

  .modal-header {
    padding: 1.25rem;
    border-radius: 20px 20px 0 0;
  }

  .header-content {
    gap: 1rem;
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    font-size: 1.4rem;
  }

  .header-text h3 {
    font-size: 1.25rem;
  }

  .header-text p {
    font-size: 0.85rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .caisse-info-section,
  .comparaison-section,
  .validation-section,
  .signature-section {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .caisse-info-section h4,
  .comparaison-section h4,
  .validation-section h4,
  .signature-section h4 {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .comparaison-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .comparaison-card {
    padding: 1rem;
  }

  .comparaison-card h5 {
    font-size: 1rem;
  }

  .montant-row {
    font-size: 0.9rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-select,
  .form-textarea {
    padding: 0.875rem;
    font-size: 16px; /* Évite le zoom sur iOS */
  }

  .signature-container {
    padding: 0.75rem;
  }

  .signature-canvas {
    height: 100px;
  }

  .signature-controls {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }

  .btn-clear {
    justify-content: center;
  }

  .signature-status {
    justify-content: center;
  }

  .modal-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 0.75rem;
  }

  .btn-cancel,
  .btn-submit,
  .btn-recu,
  .btn-cloturer {
    width: 100%;
    justify-content: center;
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .modal-container {
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-header {
    padding: 1rem;
    border-radius: 0;
  }

  .icon-wrapper {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .header-text h3 {
    font-size: 1.1rem;
  }

  .modal-body {
    padding: 0.75rem;
  }

  .caisse-info-section,
  .comparaison-section,
  .validation-section,
  .signature-section {
    padding: 0.875rem;
  }

  .modal-footer {
    padding: 0.875rem;
  }
}
</style>
